import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { createVariant, updateVariant } from '../../../shared/api/products.js'
import type { ProductVariant } from '../../../shared/types/models.js'
import {
  normalizePrice,
  normalizeSortOrder,
  validatePriceField,
  validateSortOrderField,
} from '../../../shared/utils/catalog.js'
import { toFieldErrors, type FieldErrors } from '../../../shared/utils/formErrors.js'

interface Props {
  organizationId: string
  productId: string
  variant?: ProductVariant | null
  disabled: boolean
  pricePrecisionMode?: string
  onDone: () => void
  onCancel: () => void
}

export default function VariantForm({
  organizationId,
  productId,
  variant,
  disabled,
  pricePrecisionMode = 'INTEGER_ONLY',
  onDone,
  onCancel,
}: Props) {
  const { t } = useTranslation()
  const isEdit = variant !== undefined && variant !== null

  const [name, setName] = useState(variant?.name ?? '')
  const [price, setPrice] = useState(variant ? String(variant.price) : '')
  const [oldPrice, setOldPrice] = useState(variant?.oldPrice != null ? String(variant.oldPrice) : '')
  const [sku, setSku] = useState(variant?.sku ?? '')
  const [sortOrder, setSortOrder] = useState(variant ? String(variant.sortOrder) : '0')
  const [isDefault, setIsDefault] = useState(variant?.isDefault ?? false)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [saving, setSaving] = useState(false)

  const idSuffix = isEdit && variant ? variant.id : 'new'
  const priceStep = pricePrecisionMode === 'HALF_UNIT' ? '0.5' : '1'

  function clearErrors() {
    if (Object.keys(errors).length > 0) setErrors({})
  }

  async function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault()
    if (disabled) return

    const fieldErrors: FieldErrors = {}
    if (!name.trim()) fieldErrors.name = t('error.fieldRequired')
    const priceError = validatePriceField(price)
    if (priceError === 'required') fieldErrors.price = t('products.priceRequired')
    else if (priceError === 'invalid') fieldErrors.price = t('products.priceInvalid')
    if (oldPrice.trim() !== '' && validatePriceField(oldPrice) === 'invalid') {
      fieldErrors.oldPrice = t('products.oldPriceInvalid')
    }
    if (validateSortOrderField(sortOrder)) fieldErrors.sortOrder = t('products.sortOrderInvalid')
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    const priceValue = normalizePrice(price)
    if (priceValue === undefined) return
    const oldPriceValue = oldPrice.trim() === '' ? undefined : normalizePrice(oldPrice)

    setSaving(true)
    setErrors({})
    try {
      if (isEdit && variant) {
        await updateVariant(organizationId, productId, variant.id, {
          name: name.trim(),
          price: priceValue,
          oldPrice: oldPriceValue ?? null,
          sku: sku.trim() || null,
          sortOrder: normalizeSortOrder(sortOrder) ?? 0,
          isDefault,
        })
      } else {
        await createVariant(organizationId, productId, {
          name: name.trim(),
          price: priceValue,
          oldPrice: oldPriceValue,
          sku: sku.trim() || undefined,
          sortOrder: normalizeSortOrder(sortOrder) ?? 0,
          isDefault,
        })
      }
      onDone()
    } catch (err: unknown) {
      setErrors(
        toFieldErrors(err, ['name', 'price', 'oldPrice', 'sku', 'sortOrder'], t('products.variantError')),
      )
      setSaving(false)
    }
  }

  return (
    <div className="variant-form settings-card">
      <h4>{isEdit ? t('products.variantEditTitle') : t('products.variantCreateTitle')}</h4>
      {errors.general && <div className="form-error-banner">{errors.general}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`variantName-${idSuffix}`}>{t('products.name')} *</label>
            <input
              id={`variantName-${idSuffix}`}
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value)
                clearErrors()
              }}
              maxLength={200}
              required
              disabled={saving || disabled}
              autoComplete="off"
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`variantPrice-${idSuffix}`}>{t('products.price')} *</label>
            <input
              id={`variantPrice-${idSuffix}`}
              type="number"
              min={0}
              step={priceStep}
              value={price}
              onChange={e => {
                setPrice(e.target.value)
                clearErrors()
              }}
              required
              disabled={saving || disabled}
            />
            {errors.price && <span className="field-error">{errors.price}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`variantOldPrice-${idSuffix}`}>{t('products.oldPrice')}</label>
            <input
              id={`variantOldPrice-${idSuffix}`}
              type="number"
              min={0}
              step={priceStep}
              value={oldPrice}
              onChange={e => {
                setOldPrice(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.oldPrice && <span className="field-error">{errors.oldPrice}</span>}
          </div>

          <div className="form-field">
            <label htmlFor={`variantSku-${idSuffix}`}>{t('products.sku')}</label>
            <input
              id={`variantSku-${idSuffix}`}
              type="text"
              value={sku}
              onChange={e => {
                setSku(e.target.value)
                clearErrors()
              }}
              maxLength={100}
              disabled={saving || disabled}
              autoComplete="off"
            />
            {errors.sku && <span className="field-error">{errors.sku}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor={`variantSortOrder-${idSuffix}`}>{t('products.sortOrder')}</label>
            <input
              id={`variantSortOrder-${idSuffix}`}
              type="number"
              min={0}
              step={1}
              value={sortOrder}
              onChange={e => {
                setSortOrder(e.target.value)
                clearErrors()
              }}
              disabled={saving || disabled}
            />
            {errors.sortOrder && <span className="field-error">{errors.sortOrder}</span>}
          </div>

          <div className="form-field variant-default-field">
            <label htmlFor={`variantDefault-${idSuffix}`}>{t('products.defaultVariant')}</label>
            <div className="variant-default-check">
              <input
                id={`variantDefault-${idSuffix}`}
                type="checkbox"
                checked={isDefault}
                onChange={e => {
                  setIsDefault(e.target.checked)
                  clearErrors()
                }}
                disabled={saving || disabled}
              />
              <span>{t('products.makeDefault')}</span>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || disabled}>
            {saving ? t('products.saving') : isEdit ? t('products.save') : t('products.addVariant')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
            {t('products.cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}